import { useEffect, useState } from 'react'
import { Copy } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { copyCredentialPassword } from '@/lib/clipboard'
import { generateRandomPassword, isValidPassword } from '@/lib/password-utils'

type ResetPasswordDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  subjectName: string
  entityLabel?: string
  isPending?: boolean
  onConfirm: (password: string) => void
}

export default function ResetPasswordDialog({
  open,
  onOpenChange,
  subjectName,
  entityLabel = 'user',
  isPending = false,
  onConfirm,
}: ResetPasswordDialogProps) {
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (!open) {
      setPassword('')
    }
  }, [open])

  const trimmedPassword = password.trim()
  const canSubmit = isValidPassword(trimmedPassword)

  const handleClose = () => {
    if (!isPending) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose()
          return
        }
        onOpenChange(true)
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            Set a temporary password for {subjectName || `this ${entityLabel}`}. They must change it at next sign in.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-center justify-between gap-3">
              <label htmlFor="reset-password-value" className="block text-sm font-medium text-foreground">
                Temporary password <span className="text-destructive">*</span>
              </label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPassword(generateRandomPassword())}
                  disabled={isPending}
                >
                  Random password
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyCredentialPassword(trimmedPassword)}
                  disabled={isPending || !trimmedPassword}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
            <Input
              id="reset-password-value"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter temporary password"
              type="text"
              autoComplete="new-password"
              disabled={isPending}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Use 6-20 characters with uppercase, lowercase, a number, and a special character.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(trimmedPassword)}
            disabled={!canSubmit || isPending}
          >
            {isPending ? 'Resetting...' : 'Reset password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
